import { useState, useEffect } from 'react';

export interface MenuItem {
  id: string;
  label: {en: string;ar: string;};
  url: string;
  type: 'internal' | 'external';
  openInNewTab?: boolean;
  description?: {en: string;ar: string;};
  visible?: boolean;
  children?: MenuItem[];
}

export interface FooterSettings {
  logoUrl: string;
  logoAlt: {en: string;ar: string;};
  logoHeight: number;
  tagline: {en: string;ar: string;};
  copyright: {en: string;ar: string;};
  signoff: {en: string;ar: string;};
  backgroundColor: string;
  brandColumnWidth: 'narrow' | 'medium' | 'wide' | 'full';
}

export interface HeaderSettings {
  logoUrlEn: string;
  logoUrlAr: string;
  logoHeight: number;
  logoAltEn: string;
  logoAltAr: string;
}

export interface FooterColumn {
  id: string;
  title: {en: string;ar: string;};
  width?: 'narrow' | 'medium' | 'wide' | 'full';
  contentType?: 'links' | 'text' | 'social' | 'html' | 'brand';
  items: MenuItem[];
  content?: {en: string;ar: string;};
  socialLinks?: {platform: string;url: string;}[];
  brandLogoUrl?: string;
  brandLogoAlt?: {en: string;ar: string;};
  brandLogoHeight?: number;
  brandTagline?: {en: string;ar: string;};
}

export interface MenuConfig {
  header: MenuItem[];
  footerColumns: FooterColumn[];
  footerSettings?: FooterSettings;
  headerSettings?: HeaderSettings;
}

export const DEFAULT_HEADER_SETTINGS: HeaderSettings = {
  logoUrlEn: "/3.svg",

  logoUrlAr: "/3.svg",

  logoHeight: 80,
  logoAltEn: 'Alex Hidden Gems',
  logoAltAr: 'أليكس هيدن جيمز'
};

export const DEFAULT_FOOTER_SETTINGS: FooterSettings = {
  logoUrl: "/3.svg",
  logoAlt: { en: 'Alex Hidden Gems', ar: 'أليكس هيدن جيمز' },
  logoHeight: 96,
  tagline: {
    en: 'Curating the Mediterranean soul of Alexandria — one hidden gem at a time.',
    ar: 'نختار لكم روح الإسكندرية المتوسطية — جوهرة خفية في كل مرة.'
  },
  copyright: {
    en: '© {year} AlexHiddenGems. All rights reserved.',
    ar: '© {year} أليكس هيدن جيمز. جميع الحقوق محفوظة.'
  },
  signoff: {
    en: 'Curated in Alexandria',
    ar: 'يُحرَّر في الإسكندرية'
  },
  backgroundColor: '#081C2C',
  brandColumnWidth: 'wide'
};

export const DEFAULT_MENUS: MenuConfig = {
  header: [
  {
    id: 'h_discover',
    label: { en: 'Discover', ar: 'اكتشف' },
    url: '/places',
    type: 'internal',
    visible: true
  },
  {
    id: 'h_todo',
    label: { en: 'Things to Do', ar: 'حاجات تعملها' },
    url: '/categories',
    type: 'internal',
    visible: true
  },
  {
    id: 'h_events',
    label: { en: 'Events', ar: 'فعاليات' },
    url: '/events',
    type: 'internal',
    visible: true
  },
  {
    id: 'h_itineraries',
    label: { en: 'Itineraries', ar: 'مسارات' },
    url: '/itineraries',
    type: 'internal',
    visible: true
  },
  {
    id: 'h_editorial',
    label: { en: 'Editorial', ar: 'مقالات' },
    url: '/editorial',
    type: 'internal',
    visible: true
  },
  {
    id: 'h_practical',
    label: { en: 'Practical Info', ar: 'معلومات تهمك' },
    url: '#',
    type: 'internal',
    visible: true,
    children: [
    {
      id: 'h_prac_overview',
      label: { en: 'Overview', ar: 'معلومات عامة' },
      description: {
        en: 'All the essentials',
        ar: 'كل المعلومات الأساسية'
      },
      url: '/practical-info',
      type: 'internal',
      visible: true
    },
    {
      id: 'h_prac_getting_here',
      label: { en: 'Getting Here', ar: 'إزاي توصل' },
      description: { en: 'Transport guide', ar: 'دليل المواصلات' },
      url: '/getting-here',
      type: 'internal',
      visible: true
    },
    {
      id: 'h_prac_phrases',
      label: { en: 'Basic Arabic Phrases', ar: 'عبارات عربية أساسية' },
      description: {
        en: 'Speak like a local',
        ar: 'اتكلم زي الإسكندرانية'
      },
      url: '/arabic-phrases',
      type: 'internal',
      visible: true
    }]

  }],

  footerColumns: [
  {
    id: 'f_col_brand',
    title: { en: '', ar: '' },
    width: 'wide',
    contentType: 'brand',
    items: [],
    brandLogoUrl: "/3.svg",

    brandLogoAlt: { en: 'Alex Hidden Gems', ar: 'أليكس هيدن جيمز' },
    brandLogoHeight: 96,
    brandTagline: {
      en: 'Curating the Mediterranean soul of Alexandria — one hidden gem at a time.',
      ar: 'نختار لكم روح الإسكندرية المتوسطية — جوهرة خفية في كل مرة.'
    }
  },
  {
    id: 'f_col_explore',
    title: { en: 'Explore', ar: 'تصفّح' },
    width: 'narrow',
    contentType: 'links',
    items: [
    {
      id: 'f_about',
      label: { en: 'About', ar: 'عنّا' },
      url: '/about',
      type: 'internal',
      visible: true
    },
    {
      id: 'f_editorial',
      label: { en: 'Editorial', ar: 'مقالات' },
      url: '/editorial',
      type: 'internal',
      visible: true
    },
    {
      id: 'f_itineraries',
      label: { en: 'Itineraries', ar: 'مسارات' },
      url: '/itineraries',
      type: 'internal',
      visible: true
    }]

  },
  {
    id: 'f_col_connect',
    title: { en: 'Connect', ar: 'تواصل' },
    width: 'narrow',
    contentType: 'links',
    items: [
    {
      id: 'f_events',
      label: { en: 'Events', ar: 'فعاليات' },
      url: '/events',
      type: 'internal',
      visible: true
    },
    {
      id: 'f_practical',
      label: { en: 'Practical Info', ar: 'معلومات تهمك' },
      url: '/practical-info',
      type: 'internal',
      visible: true
    },
    {
      id: 'f_getting_here',
      label: { en: 'Getting Here', ar: 'إزاي توصل' },
      url: '/getting-here',
      type: 'internal',
      visible: true
    },
    {
      id: 'f_phrases',
      label: { en: 'Basic Arabic Phrases', ar: 'عبارات عربية أساسية' },
      url: '/arabic-phrases',
      type: 'internal',
      visible: true
    },
    {
      id: 'f_contact',
      label: { en: 'Contact', ar: 'تواصل معنا' },
      url: '/contact',
      type: 'internal',
      visible: true
    },
    {
      id: 'f_submit',
      label: { en: 'Submit a Gem', ar: 'اقترح مكاناً' },
      url: '/submit-gem',
      type: 'internal',
      visible: true
    }]

  }],

  footerSettings: DEFAULT_FOOTER_SETTINGS
};

const STORAGE_KEY = 'ahg.menus.v1';

export function loadMenus(): MenuConfig {
  if (typeof window === 'undefined') return DEFAULT_MENUS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_MENUS;
    const parsed = JSON.parse(raw);

    // Merge defaults for backward compatibility
    const footerSettings = {
      ...DEFAULT_FOOTER_SETTINGS,
      ...(parsed.footerSettings || {})
    };

    const headerSettings = {
      ...DEFAULT_HEADER_SETTINGS,
      ...(parsed.headerSettings || {})
    };

    let footerColumns = (parsed.footerColumns || []).map((col: any) => ({
      ...col,
      width: col.width || 'narrow',
      contentType: col.contentType || 'links'
    }));

    // Migrate: if no brand column exists, inject one using legacy settings
    if (
    !footerColumns.some((col: FooterColumn) => col.contentType === 'brand'))
    {
      footerColumns.unshift({
        id: `f_col_brand_migrated_${Date.now()}`,
        title: { en: '', ar: '' },
        width: footerSettings.brandColumnWidth || 'wide',
        contentType: 'brand',
        items: [],
        brandLogoUrl: footerSettings.logoUrl,
        brandLogoAlt: footerSettings.logoAlt,
        brandLogoHeight: footerSettings.logoHeight,
        brandTagline: footerSettings.tagline
      });
    }

    return {
      ...parsed,
      footerSettings,
      headerSettings,
      footerColumns
    };
  } catch {
    return DEFAULT_MENUS;
  }
}

export function saveMenus(config: MenuConfig) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    window.dispatchEvent(new Event('ahg.menus.changed'));
  } catch {

    // ignore
  }}

export function useMenus() {
  const [menus, setMenus] = useState<MenuConfig>(loadMenus());

  useEffect(() => {
    const handleChanged = () => setMenus(loadMenus());
    window.addEventListener('ahg.menus.changed', handleChanged);
    return () => window.removeEventListener('ahg.menus.changed', handleChanged);
  }, []);

  return menus;
}